import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { LoggerService } from '../../core/services/logger.service';
import { LoginService } from '../../core/services/login.service';
import { ContactCenterPFService } from 'src/app/data/CXOne/services/contact-center-pf.service';
import { DynamicsPessoaFisica } from 'src/app/data/Dynamics/pessoa-fisica/dynamics-pessoa-fisica.service';
import {
  VoiceCallDirection,
  VoiceCallEventDTO,
  VoiceCallEventType,
} from './domain/ContactCenter/VoiceCallEventDTO';
import { modalUraAutenticadora } from 'src/app/shared/services/modal-ura-autenticadora.services';
import {
  DigitalSessionEventType,
  DigitalSessionEventDTO,
  DigitalSessionDirection,
  DigitalSessionEndReason,
} from './domain/ContactCenter/DigitalSessionEventDTO';
import { SendReloadScreenSignalService } from 'src/app/shared/services/sendReloadScreenSignalService';
import { ResizeButtonsComponent } from '../resize-buttons/resize-buttons.component';
import { DynamicsPessoaFisicaResizePocService } from './services/dynamics-pessoa-fisica-resize-poc.service';
import { InteractionDockResizePocService } from './services/interaction-dock-resize-poc.service';
import { ChatModalService } from 'src/app/shared/services/chat-modal.service';
import { DigitalChatSessionTimeoutManager } from '../../shared/services/digital-chat-session-timeout-manager.service';
import { CXoneDigitalClient } from '@nice-devone/digital-sdk';
import { PopUrlService } from 'src/app/data/CXOne/services/pop-url.service';
import { LocalStorageUtils } from 'src/app/data/CXOne/util/LocalStorageUtils';

@Component({
  selector: 'app-pessoa-fisica-resize-poc',
  standalone: true,
  imports: [ResizeButtonsComponent],
  providers: [
    {
      provide: DynamicsPessoaFisica,
      useClass: DynamicsPessoaFisicaResizePocService,
    },
    InteractionDockResizePocService,
    DigitalChatSessionTimeoutManager,
  ],
  templateUrl: './pessoa-fisica-resize-poc.component.html',
  styleUrl: './pessoa-fisica-resize-poc.component.css',
})
export class PessoaFisicaResizePocComponent implements OnInit, OnDestroy {
  private agentNextState = '';
  agentName = 'Carregando...';
  private isReloadedSession = false;
  private readonly localStorageUtils = new LocalStorageUtils();

  constructor(
    private popUrlService: PopUrlService,
    private logger: LoggerService,
    private crmPFService: DynamicsPessoaFisica,
    private loginService: LoginService,
    private contactCenterPFService: ContactCenterPFService,
    private modalUraAuth: modalUraAutenticadora,
    private sendReloadScreenSignalService: SendReloadScreenSignalService,
    private interactionDockService: InteractionDockResizePocService,
    private chatModalService: ChatModalService,
    private chatSessionTimeoutManager: DigitalChatSessionTimeoutManager,
  ) {
    this.isReloadedSession = sendReloadScreenSignalService.interactionIdExist();
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent): void {
    this.sendReloadScreenSignalService.sendReloadSignal();
    event.preventDefault();
    event.returnValue = '';
  }

  ngOnInit(): void {
    void this.initialize();
  }

  ngOnDestroy(): void {
    this.chatSessionTimeoutManager.dispose();
  }

  private async initialize(): Promise<void> {
    this.logger.info('PF - inicializado.');
    const centralId = this.localStorageUtils.safeGetItem('centralId') || 'pf';
    const authServiceType =
      this.localStorageUtils.safeGetItem('authServiceType') || 'nice';
    const authMode =
      this.localStorageUtils.safeGetItem('display_mode') || 'popup';

    try {
      const isAuthenticated = await this.loginService.login(
        authServiceType,
        centralId,
        true,
        authMode,
      );

      if (isAuthenticated) {
        try {
          sessionStorage.setItem('msdyn_ciDebug', 'true');
        } catch {
          this.logger.warn('Falha ao salvar msdyn_ciDebug no sessionStorage.');
        }
        this.logger.info('Usuario autenticado.');

        await this.contactCenterPFService.initializer();
        this.chatSessionTimeoutManager.initEvents(
          CXoneDigitalClient.instance.digitalContactManager,
        );

        this.setAgentName();
        this.logger.info(
          'Serviço de Contact Center PF inicializado com sucesso.',
        );

        await this.crmPFService.initialize();

        this.popUrlService.initialize();
        this.chatSessionTimeoutManager.initialize(
          this.popUrlService,
          this.chatModalService,
        );

        this.logger.info('Serviço de Dynamics PF inicializado com sucesso.');
      }
    } catch (error) {
      this.logger.error(error);
      window.alert('Falha no login.');
    }

    this.addEvents();
    this.logger.info('Eventos adicionados com sucesso.');
    this.logger.info('Inicialização concluída.');
  }

  private setAgentName() {
    const ccName = this.contactCenterPFService.getAgentName();
    this.logger.info('Nome do agente recuperado:', ccName);

    if (ccName != null) {
      this.agentName = ccName;
    }
  }

  private addEvents() {
    this.contactCenterPFService.onAgentStatusEvent$.subscribe(
      (agentStateEvent) => {
        this.logger.info(
          'Evento do Agente State recebido',
          JSON.stringify(agentStateEvent),
        );
        this.agentNextState = agentStateEvent.agentNextState;
        this.modalUraAuth.controlModalWithDefaultMessageAndTimer(
          this.agentNextState,
        );
      },
    );

    this.contactCenterPFService.onVoiceCallEvent$.subscribe(
      (voiceCallEvent) => {
        this.logger.info(
          'Evento de voz recebido: ',
          JSON.stringify(voiceCallEvent),
        );

        switch (voiceCallEvent.CallStatus) {
          case VoiceCallEventType.Answered: {
            this.OnVoiceCallAnswered(voiceCallEvent);
            break;
          }
          case VoiceCallEventType.Released: {
            this.OnVoiceCallReleased(voiceCallEvent);
          }
        }
      },
    );

    this.contactCenterPFService.onFocusEvent$.subscribe((FocusEvent) => {
      this.logger.info(
        'Solicitação de foco recebida: ',
        JSON.stringify(FocusEvent),
      );

      try {
        this.localStorageUtils.safeSetItem('interactionFocused', FocusEvent.Id);
      } catch {
        this.logger.warn('Falha ao salvar interactionFocused no localStorage.');
      }
      this.crmPFService.ChangeSessionFocus(FocusEvent.Id);
    });

    this.contactCenterPFService.onDigitalSessionEvent$.subscribe(
      (digitalSessionEvent) => {
        this.logger.info(
          'Evento de digital recebido: ',
          JSON.stringify(digitalSessionEvent),
        );

        switch (digitalSessionEvent.SessionEventType) {
          case DigitalSessionEventType.Assigned: {
            if (
              digitalSessionEvent.SessionDirection ===
              DigitalSessionDirection.Inbound
            ) {
              this.OnDigitalSessionReceived(digitalSessionEvent);
            }
            break;
          }
          case DigitalSessionEventType.Unassigned: {
            if (
              digitalSessionEvent.SessionDirection ===
              DigitalSessionDirection.Inbound
            ) {
              this.OnDigitalSessionEnded(digitalSessionEvent);
            }
            break;
          }
        }
      },
    );
    this.crmPFService.onAddUserIdOnAttachData$.subscribe(async (command) => {
      this.contactCenterPFService.AddCrmUserIdOnAttachData(
        command.interactionId,
        command.crmUserId,
      );
    });

    this.interactionDockService.onVoiceCallRingingEvent$.subscribe(
      async (ringingEvent) => {
        this.logger.info(
          'PF RESIZE POC - Chamada tocando recebida: ',
          JSON.stringify(ringingEvent),
        );

        await this.dockCIFForNewInteractionIfConfigured();
      },
    );

    this.interactionDockService.onDigitalInteractionEvent$.subscribe(
      async (digitalInteractionEvent) => {
        this.logger.info(
          'PF RESIZE POC - Interação digital recebida: ',
          JSON.stringify(digitalInteractionEvent),
        );

        await this.dockCIFForNewInteractionIfConfigured();
      },
    );

    this.interactionDockService.start();
  }

  private async OnDigitalSessionReceived(
    digitalSessionEvent: DigitalSessionEventDTO,
  ) {
    await this.dockCIFForNewInteractionIfConfigured();

    await this.crmPFService.openTicket(
      digitalSessionEvent.Id,
      digitalSessionEvent.AttachData,
      false,
      digitalSessionEvent.Id,
      digitalSessionEvent.mediaType,
    );
    this.logger.info('Ticket aberto com sucesso.');
  }

  private async OnDigitalSessionEnded(
    digitalSessionEvent: DigitalSessionEventDTO,
  ) {
    const contactId =
      digitalSessionEvent.contactId?.trim() || digitalSessionEvent.Id;

    this.chatSessionTimeoutManager.signalClosed(contactId);

    if (
      digitalSessionEvent.SessionEndReason == DigitalSessionEndReason.Closed
    ) {
      await this.crmPFService.endTicket(
        digitalSessionEvent.Id,
        digitalSessionEvent.AttachData,
        digitalSessionEvent.mediaType,
      );
    }

    if (
      digitalSessionEvent.SessionEndReason == DigitalSessionEndReason.Transfered
    ) {
      await this.crmPFService.customerTransfered(
        digitalSessionEvent.Id,
        digitalSessionEvent.AttachData,
        digitalSessionEvent.mediaType,
      );
    }
  }

  private async OnVoiceCallAnswered(voiceCallEvent: VoiceCallEventDTO) {
    if (
      voiceCallEvent.CallDirection === VoiceCallDirection.Outbound &&
      !voiceCallEvent.AttachData?.CALLBACK
    ) {
      this.logger.info(
        `PF (ACD - Contato não é inbound, não executando a criação de ticket no dynamics`,
      );
      return;
    }

    // O dock da voz acontece no toque (onVoiceCallRingingEvent$), não aqui.
    await this.crmPFService.openTicket(
      voiceCallEvent.Id,
      voiceCallEvent.AttachData,
      true,
      voiceCallEvent.Id,
      'voice',
    );
    this.sendReloadScreenSignalService.setInteractionIdInLocalStorage(
      voiceCallEvent.Id,
    );
  }

  private async dockCIFForNewInteractionIfConfigured(): Promise<void> {
    if (this.crmPFService instanceof DynamicsPessoaFisicaResizePocService) {
      await this.crmPFService.onNewInteractionCIFControl();
      return;
    }

    this.logger.warn(
      'PF RESIZE POC - Dock ignorado: o serviço de CRM injetado não é o da POC.',
    );
  }

  private async OnVoiceCallReleased(voiceCallEvent: VoiceCallEventDTO) {
    if (
      voiceCallEvent.CallDirection === VoiceCallDirection.Outbound &&
      !voiceCallEvent.AttachData?.CALLBACK
    ) {
      this.logger.info(
        `PF (ACD - Contato não é inbound e não é callback, não executando o encerramento de ticket no dynamics`,
      );
      return;
    }

    if (this.agentNextState === 'Autenticadora') {
      this.logger.info('Transferência para Ura detectada');
      await this.crmPFService.customerTransferedAuthIVR(
        voiceCallEvent.Id,
        voiceCallEvent.AttachData,
        'voice',
      );
      return;
    }

    if (voiceCallEvent.DisconectCode === 'TransferComplete') {
      this.logger.info('Transferência detectada');
      await this.crmPFService.customerTransfered(
        voiceCallEvent.Id,
        voiceCallEvent.AttachData,
        'voice',
      );
      return;
    }

    if (this.isReloadedSession === true) {
      this.sendReloadScreenSignalService.removeInteractionIdInLocalStorage();
      this.isReloadedSession = false;
    } else {
      await this.crmPFService.endTicket(
        voiceCallEvent.Id,
        voiceCallEvent.AttachData,
        'voice',
      );
    }
  }
}
